import type { BadgeColour } from '@lmc/design-system'
import { tokens } from '@lmc/design-system/tokens'
import type {
  Content,
  ContentText,
  Margins,
  Node,
  StyleDictionary,
  TDocumentDefinitions
} from 'pdfmake/interfaces'
import { DOWNLOAD_TIMEOUT_MS, downloadPdf } from '../downloadPdf'
import Logo from '../Logo'
import type {
  PdfBlock,
  PdfDocument,
  PdfImages,
  PdfList,
  PdfPerson,
  PdfRow
} from './pdfDocument'

const { color } = tokens

/** A4 in points, with the design's narrow side margins. */
const PAGE_WIDTH = 595
const PAGE_MARGIN_X = 16
const PAGE_MARGIN_Y = 32
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN_X * 2

const COLUMN_GAP = 16
const LOGO_SIZE: [number, number] = [50, 10]
const PHOTO_SIZE = 40
/** Tallest a picture prints, so one and its caption fit well within a page. */
const MAX_IMAGE_HEIGHT = 320

const MAX_FILE_NAME_LENGTH = 150

/** Headings are levels 1 and 2; a label over its content is this level. */
const LABEL_LEVEL = 3

/** Printed content: a leaf with text that isn't a heading or label. */
const isContent = (node: Node) => !node.headlineLevel && node.text !== undefined

/**
 * Moves a heading or label onto the next page when nothing it introduces
 * follows it on this one. Wrapping stacks and columns aren't content, so only
 * printed text counts; with nothing on the next page either, it stays.
 */
export const keepWithNext = (
  node: Node,
  followingNodesOnPage: Array<Node>,
  nodesOnNextPage: Array<Node>
) =>
  Boolean(node.headlineLevel) &&
  !followingNodesOnPage.some(isContent) &&
  nodesOnNextPage.some(isContent)

const styles: StyleDictionary = {
  caption: { color: color.text.secondary, fontSize: 10, margin: [0, 4, 0, 0] },
  entryHeading: { bold: true, fontSize: 14, margin: [0, 4, 0, 12] },
  label: { color: color.text.secondary, margin: [0, 0, 0, 2] },
  running: { color: color.text.secondary, fontSize: 8 },
  sectionHeading: { bold: true, fontSize: 16, margin: [0, 0, 0, 12] },
  title: { bold: true, fontSize: 18 }
}

/** pdfmake breaks lines on `\n` only, so other line endings are normalised. */
const withLineBreaks = (text: string) => text.replace(/\r\n?/g, '\n')

const rule = (lineColor: string, margin: Margins): Content => ({
  canvas: [
    {
      lineColor,
      lineWidth: 1,
      type: 'line',
      x1: 0,
      x2: CONTENT_WIDTH,
      y1: 0,
      y2: 0
    }
  ],
  margin
})

/** The line between sections. */
const divider = () => rule(color.border.input.default, [0, 4, 0, 16])

/** The design's badge colours, as the design system's Badge maps them. */
const badgeColours = (colour: BadgeColour) => {
  const tone = colour === 'grey' ? 'neutral' : colour
  return {
    background: color.bg.accent.subtle[tone],
    border: color.border.accent[tone],
    text: color.text.accent[colour]
  }
}

/**
 * Text in a bordered, filled box sized to it. pdfmake can't round a table's
 * corners, so the badge is square.
 */
const badge = (text: string, colour: BadgeColour): Content => {
  const { background, border, text: textColour } = badgeColours(colour)
  return {
    layout: {
      fillColor: () => background,
      hLineColor: () => border,
      paddingBottom: () => 2,
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 2,
      vLineColor: () => border
    },
    table: { body: [[{ color: textColour, text }]], widths: ['auto'] }
  }
}

const heading = (text: string, level: number): ContentText => ({
  headlineLevel: level,
  style: level === 1 ? 'sectionHeading' : 'entryHeading',
  text: withLineBreaks(text)
})

/**
 * A block split for page breaks: `head` is its first unit (the whole of a
 * short row, a label with the first line or item of what it labels, a
 * heading with the head of its first block), which must not be split from
 * what comes before it; `rest` may break anywhere.
 */
type Parts = { head: Array<Content>; rest: Array<Content> }

/**
 * Longer than this, a first unit could be taller than a page, so it is left
 * breakable rather than overflow; its heading's `keepWithNext` still applies.
 */
const MAX_UNIT_LENGTH = 400
const MAX_UNIT_LINES = 8

const isShort = (text: string) =>
  text.length <= MAX_UNIT_LENGTH && text.split('\n').length <= MAX_UNIT_LINES

const GAP: Margins = [0, 0, 0, 12]

/** Head and rest as content: the head as one unbreakable unit. */
const joined = ({ head, rest }: Parts): Array<Content> => [
  ...(head.length ? [{ stack: head, unbreakable: true }] : []),
  ...rest
]

const label = (text: string): ContentText => ({
  headlineLevel: LABEL_LEVEL,
  style: 'label',
  text: withLineBreaks(text)
})

/** A label over pieces, the first kept with the label when it is short. */
const labelledParts = (
  text: string | undefined,
  [first, ...more]: Array<Content>,
  firstIsShort: boolean
): Parts => {
  const labelNodes = text ? [label(text)] : []
  const last = (content: Content, isLast: boolean): Content =>
    isLast ? { margin: GAP, stack: [content] } : content
  const restNodes = more.map((content, index) =>
    last(content, index === more.length - 1)
  )
  const firstNode = last(first, more.length === 0)

  return firstIsShort
    ? { head: [...labelNodes, firstNode], rest: restNodes }
    : { head: labelNodes, rest: [firstNode, ...restNodes] }
}

/** Short enough to sit in half the page, held whole. */
type Pairable = PdfList | PdfRow

const isPairable = (block: PdfBlock): block is Pairable => {
  if (block.kind === 'row') {
    return isShort(withLineBreaks(block.value))
  }
  if (block.kind === 'list') {
    return isShort(block.items.map(withLineBreaks).join('\n'))
  }
  return false
}

/**
 * A pairable block in its half. The pair is held whole, so its label needs
 * no `keepWithNext`.
 */
const halfContent = (block: Pairable): Array<Content> => {
  const labelText = block.label
  const labelNodes: Array<Content> = labelText
    ? [{ style: 'label', text: withLineBreaks(labelText) }]
    : []
  if (block.kind === 'list') {
    return [...labelNodes, { ul: block.items.map(withLineBreaks) }]
  }
  return [
    ...labelNodes,
    block.colour
      ? badge(block.value, block.colour)
      : { text: withLineBreaks(block.value) }
  ]
}

/** Two short answers side by side, or one in the left half. */
const pairParts = (left: Pairable, right?: Pairable): Parts => ({
  head: [
    {
      columnGap: COLUMN_GAP,
      columns: [
        { stack: halfContent(left), width: '*' },
        right
          ? { stack: halfContent(right), width: '*' }
          : { text: '', width: '*' }
      ],
      margin: GAP
    }
  ],
  rest: []
})

const personParts = ({ name, photo }: PdfPerson): Parts => ({
  head: [
    photo
      ? {
          columnGap: 12,
          columns: [
            { fit: [PHOTO_SIZE, PHOTO_SIZE], image: photo, width: PHOTO_SIZE },
            {
              margin: [0, 13, 0, 0],
              text: withLineBreaks(name),
              width: '*'
            }
          ],
          margin: GAP
        }
      : { margin: GAP, text: withLineBreaks(name) }
  ],
  rest: []
})

/** A picture with its caption, never split across pages. */
const imageContent = ({
  caption,
  src
}: PdfImages['images'][number]): Content => ({
  stack: [
    { fit: [CONTENT_WIDTH, MAX_IMAGE_HEIGHT], image: src },
    ...(caption ? [{ style: 'caption', text: withLineBreaks(caption) }] : [])
  ],
  unbreakable: true
})

/** One block on its own; pairs are laid out by `unitsOf`. */
function blockParts(block: PdfBlock): Parts {
  switch (block.kind) {
    case 'entry':
      return headedParts([heading(block.heading, 2)], block.blocks)
    case 'line':
      // Complete in itself, so it may end a page.
      return {
        head: [{ style: 'entryHeading', text: withLineBreaks(block.text) }],
        rest: []
      }
    case 'list': {
      const [first = '', ...more] = block.items.map(withLineBreaks)
      return labelledParts(
        block.label,
        more.length ? [{ ul: [first] }, { ul: more }] : [{ ul: [first] }],
        isShort(first)
      )
    }
    case 'images':
      // Each picture is short enough to keep with the label.
      return labelledParts(block.label, block.images.map(imageContent), true)
    case 'person':
      return personParts(block)
    case 'row':
    case 'text': {
      const [first, ...more] = withLineBreaks(block.value).split('\n')
      return labelledParts(
        block.label,
        more.length
          ? [{ text: first }, { text: more.join('\n') }]
          : [{ text: first }],
        isShort(first)
      )
    }
  }
}

/**
 * Blocks as units in reading order: consecutive short rows and lists two to
 * a line, as the design lays out its fields, and anything else on its own.
 */
const unitsOf = (blocks: ReadonlyArray<PdfBlock>): Array<Parts> => {
  const units: Array<Parts> = []
  let index = 0
  while (index < blocks.length) {
    const block = blocks[index]
    if (isPairable(block)) {
      const next = blocks[index + 1]
      const right = next && isPairable(next) ? next : undefined
      units.push(pairParts(block, right))
      index += right ? 2 : 1
    } else {
      units.push(blockParts(block))
      index += 1
    }
  }
  return units
}

const headedParts = (
  headingNodes: Array<Content>,
  blocks: ReadonlyArray<PdfBlock>
): Parts => {
  const [first = { head: [], rest: [] }, ...others] = unitsOf(blocks)
  return {
    head: [...headingNodes, ...first.head],
    rest: [...first.rest, ...others.flatMap(joined)]
  }
}

const titleContent = ({ title, titleBadge }: PdfDocument): Content => ({
  columnGap: COLUMN_GAP,
  columns: [
    { style: 'title', text: withLineBreaks(title), width: '*' },
    ...(titleBadge
      ? [{ stack: [badge(titleBadge.text, titleBadge.colour)], width: 'auto' }]
      : [])
  ],
  margin: [0, 0, 0, 12]
})

/**
 * Lays any document out for pdfmake: the title, then each section under a
 * divider, with a running header and footer on every page. Every heading
 * and label is held with the first unit of what it introduces (`Parts`), and
 * `keepWithNext` backs that up where the unit is too long to hold; pdfmake
 * paginates the rest, breaking long text.
 */
export const toPdfDocDefinition = (
  document: PdfDocument
): TDocumentDefinitions => ({
  content: [
    titleContent(document),
    ...document.sections.flatMap((section) =>
      joined(
        headedParts([divider(), heading(section.heading, 1)], section.blocks)
      )
    )
  ],
  defaultStyle: { color: color.text.primary, fontSize: 12 },
  footer: (currentPage) => ({
    margin: [PAGE_MARGIN_X, 0, PAGE_MARGIN_X, 0],
    stack: [
      rule(color.border.subtle, [0, 0, 0, 6]),
      {
        columnGap: 8,
        columns: [
          { fit: LOGO_SIZE, image: Logo, width: LOGO_SIZE[0] },
          {
            alignment: 'center',
            style: 'running',
            text: document.footer,
            width: '*'
          },
          {
            alignment: 'right',
            style: 'running',
            text: document.pageLabel(currentPage),
            width: LOGO_SIZE[0]
          }
        ]
      }
    ]
  }),
  header: () => ({
    margin: [PAGE_MARGIN_X, 8, PAGE_MARGIN_X, 0],
    stack: [
      {
        columnGap: 8,
        columns: [
          { style: 'running', text: document.header?.start ?? '', width: '*' },
          {
            alignment: 'right',
            style: 'running',
            text: document.header?.end ?? '',
            width: '*'
          }
        ]
      },
      rule(color.border.subtle, [0, 4, 0, 0])
    ]
  }),
  info: { title: document.title },
  pageBreakBefore: keepWithNext,
  pageMargins: [PAGE_MARGIN_X, PAGE_MARGIN_Y, PAGE_MARGIN_X, PAGE_MARGIN_Y],
  pageSize: 'A4',
  styles
})

/**
 * A safe file name ending in `.pdf`: characters a file system rejects become
 * `-`, and a dot in the name can't be taken for the extension.
 */
export const toPdfFileName = (name: string) => {
  const safe = name
    // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this strips
    .replace(/[\\/:*?"<>|\u0000-\u001F]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/\.pdf$/i, '')
    .slice(0, MAX_FILE_NAME_LENGTH)
    .replace(/[\s.]+$/, '')
    .trim()

  return `${safe || 'document'}.pdf`
}

/**
 * Downloads a document as a PDF with the app's shared pdfmake download,
 * resolving once the file is handed to the browser. Rejects if pdfmake fails
 * or takes longer than `timeoutMs`.
 */
export const downloadPdfDocument = (
  document: PdfDocument,
  fileName: string,
  timeoutMs = DOWNLOAD_TIMEOUT_MS
) =>
  downloadPdf(toPdfDocDefinition(document), toPdfFileName(fileName), timeoutMs)
